import { createContext, useContext } from "react";

export type TabsValue = string;
export type TabsVariant = "blue" | "neutral" | "underline";

export type TabsStyleConfig = {
  variant?: TabsVariant;
  listClassName?: string;
  triggerClassName?: string;
  activeTriggerClassName?: string;
  panelClassName?: string;
};

type TabsContextValueBase = {
  value: TabsValue;
  setValue: (v: TabsValue) => void;
  styles: Required<TabsStyleConfig>;
};

const DEFAULT_STYLES: Required<TabsStyleConfig> = {
  variant: "blue",
  listClassName: "",
  triggerClassName: "",
  activeTriggerClassName: "",
  panelClassName: "",
};

export const mergeTabsStyles = (
  styles?: TabsStyleConfig,
): Required<TabsStyleConfig> => ({
  ...DEFAULT_STYLES,
  ...(styles ?? {}),
});

export const TabsContext = createContext<TabsContextValueBase | null>(null);

export const useTabsContext = <T extends TabsValue>() => {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error("Tabs components must be used inside <TabsRoot />");
  }

  // Re-type to the narrower union for consumers
  return {
    value: ctx.value as T,
    setValue: ctx.setValue as (v: T) => void,
    styles: ctx.styles,
  };
};
